import { useState, useEffect, useCallback } from 'react';
import {
  getMatchAlerts,
  resolveMatchAlert,
  triggerMatchScan,
  MatchAlert,
  ResolveAlertRequest,
} from '../services/investigationApi';

function MatchAlertsPage() {
  const [alerts, setAlerts] = useState<MatchAlert[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Filters
  const [filterType, setFilterType] = useState('');
  const [filterSeverity, setFilterSeverity] = useState('');
  const [filterStatus, setFilterStatus] = useState('');

  // Resolution dialog
  const [resolvingAlert, setResolvingAlert] = useState<MatchAlert | null>(null);
  const [resolutionType, setResolutionType] = useState<ResolveAlertRequest['resolution']>('FALSE_POSITIVE');
  const [resolutionNote, setResolutionNote] = useState('');
  const [resolving, setResolving] = useState(false);

  // Scan
  const [scanning, setScanning] = useState(false);

  // Check if user is admin
  const userStr = localStorage.getItem('user');
  const user = userStr ? JSON.parse(userStr) : null;
  const isAdmin = user?.role === 'admin';

  const fetchAlerts = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getMatchAlerts({
        type: filterType || undefined,
        severity: filterSeverity || undefined,
        status: filterStatus || undefined,
      });
      setAlerts(data.alerts || []);
      setTotal(data.total || 0);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [filterType, filterSeverity, filterStatus]);

  useEffect(() => {
    fetchAlerts();
  }, [fetchAlerts]);

  const handleResolve = async () => {
    if (!resolvingAlert) return;
    setResolving(true);
    try {
      await resolveMatchAlert(resolvingAlert.id, {
        resolution: resolutionType,
        resolution_note: resolutionNote || undefined,
      });
      setResolvingAlert(null);
      setResolutionNote('');
      fetchAlerts();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setResolving(false);
    }
  };

  const handleScan = async () => {
    setScanning(true);
    setError(null);
    try {
      const result = await triggerMatchScan();
      alert(`Scan terminé. ${result.total_created} nouvelle(s) alerte(s) créée(s). Batch: ${result.batch_id}`);
      fetchAlerts();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setScanning(false);
    }
  };

  const severityBadge = (severity: string) => {
    const colors: Record<string, string> = {
      HIGH: 'bg-red-100 text-red-700',
      CRITICAL: 'bg-red-200 text-red-800',
      MEDIUM: 'bg-yellow-100 text-yellow-700',
      LOW: 'bg-gray-100 text-gray-700',
    };
    return colors[severity] || 'bg-gray-100 text-gray-600';
  };

  const statusBadge = (status: string) => {
    const colors: Record<string, string> = {
      OPEN: 'bg-blue-100 text-blue-700',
      INVESTIGATING: 'bg-purple-100 text-purple-700',
      RESOLVED: 'bg-green-100 text-green-700',
      DISMISSED: 'bg-gray-100 text-gray-500',
    };
    return colors[status] || 'bg-gray-100 text-gray-600';
  };

  const alertTypeLabel = (type: string) => {
    const labels: Record<string, string> = {
      SAME_PHONE_MULTIPLE_IDENTITIES: 'Même téléphone',
      SAME_DOCUMENT_MULTIPLE_FILES: 'Même document',
      SIMILAR_NAME_DETECTED: 'Nom similaire',
    };
    return labels[type] || type;
  };

  return (
    <div className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
      <div className="flex justify-between items-start mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">⚠️ Alertes de Matching</h1>
          <p className="text-gray-600">Correspondances d'identité détectées</p>
        </div>
        {isAdmin && (
          <button onClick={handleScan} disabled={scanning}
            className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:opacity-50 font-medium text-sm">
            {scanning ? 'Scan en cours...' : '🔄 Lancer un scan'}
          </button>
        )}
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow p-4 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label htmlFor="filter-alert-type" className="block text-sm font-medium text-gray-700 mb-1">Type</label>
            <select id="filter-alert-type" value={filterType} onChange={(e) => setFilterType(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500">
              <option value="">Tous</option>
              <option value="SAME_PHONE_MULTIPLE_IDENTITIES">Même téléphone</option>
              <option value="SAME_DOCUMENT_MULTIPLE_FILES">Même document</option>
              <option value="SIMILAR_NAME_DETECTED">Nom similaire</option>
            </select>
          </div>
          <div>
            <label htmlFor="filter-alert-severity" className="block text-sm font-medium text-gray-700 mb-1">Sévérité</label>
            <select id="filter-alert-severity" value={filterSeverity} onChange={(e) => setFilterSeverity(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500">
              <option value="">Toutes</option>
              <option value="LOW">Low</option>
              <option value="MEDIUM">Medium</option>
              <option value="HIGH">High</option>
            </select>
          </div>
          <div>
            <label htmlFor="filter-alert-status" className="block text-sm font-medium text-gray-700 mb-1">Statut</label>
            <select id="filter-alert-status" value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500">
              <option value="">Tous</option>
              <option value="OPEN">Open</option>
              <option value="INVESTIGATING">Investigating</option>
              <option value="RESOLVED">Resolved</option>
              <option value="DISMISSED">Dismissed</option>
            </select>
          </div>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6 text-red-700">{error}</div>
      )}

      {/* Alerts Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="px-6 py-3 border-b border-gray-200">
          <span className="text-sm text-gray-600">{total} alerte(s)</span>
        </div>
        {loading ? (
          <div className="p-8 text-center text-gray-500">Chargement...</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Sévérité</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Statut</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Personne 1</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Personne 2</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Score</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Détecté le</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {alerts.length === 0 ? (
                  <tr><td colSpan={8} className="px-4 py-8 text-center text-gray-400">Aucune alerte</td></tr>
                ) : alerts.map((a) => (
                  <tr key={a.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm">{alertTypeLabel(a.alert_type)}</td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${severityBadge(a.severity)}`}>{a.severity}</span>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${statusBadge(a.status)}`}>{a.status}</span>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">
                      {a.primary_guest_name || a.primary_guest_id.slice(0, 8) + '...'}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">
                      {a.secondary_guest_name || a.secondary_guest_id.slice(0, 8) + '...'}
                    </td>
                    <td className="px-4 py-3 text-sm font-mono">{(a.match_score * 100).toFixed(1)}%</td>
                    <td className="px-4 py-3 text-sm text-gray-500">{new Date(a.detected_at).toLocaleDateString()}</td>
                    <td className="px-4 py-3 text-sm">
                      {(a.status === 'OPEN' || a.status === 'INVESTIGATING') && (
                        <button onClick={() => { setResolvingAlert(a); setResolutionType('FALSE_POSITIVE'); setResolutionNote(''); }}
                          className="text-blue-600 hover:text-blue-800 text-sm font-medium">Résoudre</button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Resolution Dialog */}
      {resolvingAlert && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md" role="dialog" aria-label="Résolution d'alerte">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Résoudre l'alerte</h3>
            <p className="text-sm text-gray-600 mb-4">
              {alertTypeLabel(resolvingAlert.alert_type)} — Score: {(resolvingAlert.match_score * 100).toFixed(1)}%
            </p>

            <div className="mb-4">
              <label htmlFor="resolution-type" className="block text-sm font-medium text-gray-700 mb-1">Type de résolution</label>
              <select id="resolution-type" value={resolutionType} onChange={(e) => setResolutionType(e.target.value as ResolveAlertRequest['resolution'])}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500">
                <option value="CONFIRMED_MATCH">Correspondance confirmée</option>
                <option value="FALSE_POSITIVE">Faux positif</option>
                <option value="MERGED">Fusionné</option>
              </select>
            </div>

            <div className="mb-4">
              <label htmlFor="resolution-note" className="block text-sm font-medium text-gray-700 mb-1">Note (optionnel)</label>
              <textarea id="resolution-note" value={resolutionNote} onChange={(e) => setResolutionNote(e.target.value)}
                rows={3} placeholder="Commentaire sur la résolution..."
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500" />
            </div>

            <div className="flex justify-end gap-3">
              <button onClick={() => setResolvingAlert(null)}
                className="px-4 py-2 text-sm text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50">Annuler</button>
              <button onClick={handleResolve} disabled={resolving}
                className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50">
                {resolving ? 'En cours...' : 'Confirmer'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default MatchAlertsPage;
